import { ShowAlertLoginErrorDecripted } from "utils/alerts";
import fernet from "fernet";
import crypto from "crypto-browserify";

/**
 * Decrypts login data using AES-256-GCM and Fernet encryption.
 * @param {string} encryptedData - The encrypted login data to be decrypted.
 * @param {Buffer} encryptedFernetKey - The encrypted Fernet key used for decryption.
 * @param {Buffer} salt - The salt used in the key derivation process.
 * @param {Buffer} nonce - The nonce used for AES-GCM decryption.
 * @param {Buffer} derivedKey - The derived key used for AES-GCM decryption.
 * @returns {Object} The decrypted login data as a JSON object.
 * @throws Will throw an error if decryption fails.
 */
const decryptLoginData = (encryptedData, encryptedFernetKey, salt, nonce, derivedKey) => {
  try {
    const aesGcm = crypto.createDecipheriv("aes-256-gcm", derivedKey, nonce);
    aesGcm.setAuthTag(encryptedFernetKey.subarray(encryptedFernetKey.length - 16));
    const fernetKey = Buffer.concat([
      aesGcm.update(encryptedFernetKey.subarray(0, encryptedFernetKey.length - 16)),
      aesGcm.final(),
    ]);

    const secret = new fernet.Secret(fernetKey.toString("utf-8"));
    const token = new fernet.Token({
      secret: secret,
      token: encryptedData,
      ttl: 0,
    });
    const decoded = token.decode();

    return JSON.parse(decoded);
  } catch (error) {
    ShowAlertLoginErrorDecripted();
    throw error;
  }
};

/**
 * Decrypts an encrypted Fernet key using AES-256-GCM.
 * @param {Uint8Array} encryptedFernetKey - The encrypted Fernet key with authentication tag appended.
 * @param {Buffer} nonce - The nonce used for encryption.
 * @param {Buffer} derivedKey - The derived key for decryption.
 * @returns {string} The decrypted Fernet key as a UTF-8 string.
 * @throws Will throw an error if decryption fails or if the encrypted data is too short.
 */
const decryptLoginFernetKey = (encryptedFernetKey, nonce, derivedKey) => {
  try {
    if (encryptedFernetKey.length < 16) {
      throw new Error("Encrypted data too short.");
    }

    const tag = encryptedFernetKey.subarray(encryptedFernetKey.length - 16);
    const ciphertext = encryptedFernetKey.subarray(0, encryptedFernetKey.length - 16);

    const decipher = crypto.createDecipheriv("aes-256-gcm", derivedKey, nonce);
    decipher.setAuthTag(tag);

    const decrypted = Buffer.concat([decipher.update(ciphertext), decipher.final()]);
    return decrypted.toString("utf-8");
  } catch (error) {
    ShowAlertLoginErrorDecripted();
    throw error;
  }
};

/**
 * Restores the original key from a scrambled key using a given pattern.
 * @param {Buffer} scrambledKey - The scrambled key as a buffer.
 * @param {Array<number>} pattern - An array representing the scrambling pattern,
 * where each index corresponds to the position in the scrambled key and its value
 * is the position in the original key.
 * @returns {Buffer} The original key restored from the scrambled key.
 */
const restoreKey = (scrambledKey, pattern) => {
  const originalKey = Buffer.alloc(scrambledKey.length);
  pattern.forEach((scrambledIndex, originalIndex) => {
    originalKey[scrambledIndex] = scrambledKey[originalIndex];
  });
  return originalKey;
};

/**
 * Decrypts an encrypted login response by restoring the derived key,
 * decoding all base64-encoded fields, and decrypting the provided token.
 * This function is typically used after receiving an encrypted payload
 * from a backend authentication endpoint. It restores the cryptographic key
 * using the provided scramble pattern and then decrypts the encrypted data.
 * @param {Object} response - The encrypted response object returned by the server.
 * @param {string} response.ed - The encrypted data/token (usually a Fernet token or similar).
 * @param {string} response.efk - The encrypted Fernet key, base64-encoded.
 * @param {string} response.s - The salt used during encryption, base64-encoded.
 * @param {string} response.n - The nonce used during encryption, base64-encoded.
 * @param {string} response.dk - The derived key used to scramble/restore the final key, base64-encoded.
 * @param {string} response.p - The scramble pattern used to restore the derived key.
 * @returns {string|null} The decrypted token or null if the response is invalid.
 */
export const decryptResponse = (response) => {
  if (!response) return null;

  const { ed, efk, s, n, dk, p: scramblePattern } = response;

  const decodedSalt = Buffer.from(s, "base64");
  const decodedNonce = Buffer.from(n, "base64");
  const decodedEncryptedFernetKey = Buffer.from(efk, "base64");
  const decodedDerivedKey = Buffer.from(dk, "base64");

  const restoredKey = restoreKey(decodedDerivedKey, scramblePattern);

  const decodedToken = decryptLoginData(
    ed,
    decodedEncryptedFernetKey,
    decodedSalt,
    decodedNonce,
    restoredKey
  );

  const fernetKey = decryptLoginFernetKey(decodedEncryptedFernetKey, decodedNonce, restoredKey);

  return {
    decodedToken,
    fernetKey,
    restoredKey,
    decodedSalt,
    decodedNonce,
    decodedEncryptedFernetKey,
  };
};
